import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Plus } from "@phosphor-icons/react"
import { useUser } from "@/apis/bff"
import { RequestUploadFilesDialog, SendRequestAfterSaving } from "@/components/request-upload-files-dialog"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { useUserData } from "@/hooks/useUserData"
import { formatDate } from "@/lib/format-date"
import { joinStrings } from "@/lib/joinString"
import { FileUploadRequestFragment$data } from "@/modules/patients/services/__generated__/FileUploadRequestFragment.graphql"
import { UploadDialog } from "@/modules/upload/upload-dialog"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { ContactChannel, FileUploadRequestType } from "@/types/__generated__/union-types"

type RequestUploadFilesMenuProps = {
  appointmentId?: string | null
  isUploadPending?: boolean
  contactChannel?: ContactChannel
  isContactChannelVerified?: boolean
  isSendingRequest?: boolean
  sendRequestAfterSaving?: SendRequestAfterSaving
  fileUploadRequestData?: FileUploadRequestFragment$data | null
  isDisabled?: boolean
  onSendRequest?: (documentType?: FileUploadRequestType, onSendRequestCompleted?: () => void) => void
  isCancelingRequest?: boolean
  onCancelRequest?: () => void
}

const RequestUploadFilesMenu = ({
  appointmentId,
  isUploadPending,
  contactChannel,
  isContactChannelVerified,
  isSendingRequest,
  sendRequestAfterSaving,
  fileUploadRequestData,
  isDisabled,
  onSendRequest,
  isCancelingRequest,
  onCancelRequest,
}: RequestUploadFilesMenuProps) => {
  const { isOneOf } = useUser()
  const isRoleAllowed = isOneOf(["CustomerServiceCenter", "MedicalSupervisor"])
  const { t } = useTranslation()
  const [isOpenDialog, setIsOpenDialog] = useState(false)
  const [isUploadOpen, setIsUploadOpen] = useState(false)
  const confirm = useConfirmStore((state) => state.confirm)
  const closeConfirmation = useConfirmStore((state) => state.cancel)
  const { createdAt, createdBy } = { ...fileUploadRequestData }
  const requestSender = useUserData(createdBy)

  const handleCancelPendingRequest = () => {
    closeConfirmation()
    onCancelRequest?.()
  }

  const openFilesHaveRequestedDialog = () => {
    confirm({
      variant: "fitContent",
      title: t("patient.uploadFiles.requestDataFromPatient"),
      content: (
        <div className="grid gap-4">
          <p className="text-on-surface-subdued text-sm">{t("patient.uploadFiles.pendingRequest.hint")}</p>
          <Banner variant="info" type="inline" className="px-4 py-3" data-testid="pending-request-banner">
            <div className="flex w-full flex-col gap-1">
              <p className="whitespace-nowrap">
                {t("patient.uploadFiles.pendingRequest.sentBy", {
                  senderName: joinStrings([requestSender?.firstName, requestSender?.lastName], " "),
                  dateTime: createdAt ? formatDate(new Date(createdAt), "dd.MM.yyyy HH:mm") : "",
                })}
              </p>
              <Button
                variant="tertiary"
                className="-mr-3 -mb-1 h-auto min-h-8 self-end px-3 py-1"
                data-testid="cancel-request-button"
                onClick={handleCancelPendingRequest}>
                {t("common.button.cancelRequest")}
              </Button>
            </div>
          </Banner>
        </div>
      ),
      isShowConfirmButton: false,
      cancelText: t("common.button.close"),
    })
  }

  const handleOpenRequestDialog = () => {
    if (isUploadPending) {
      openFilesHaveRequestedDialog()
      return
    }
    setIsOpenDialog(true)
  }

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap gap-2">
        <div>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild disabled={isDisabled}>
              <Button data-testid="request-upload-files-open-menu-button" variant="tertiary" className="h-10 w-10 p-2">
                <Plus className="text-on-surface-subdued" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="min-w-66.5 rounded-lg p-2"
              onMouseDown={(e) => e.stopPropagation()}>
              <DropdownMenuItem
                data-testid="request-patient-upload-files-button"
                className="focus:bg-surface-hovered rounded-md py-2"
                onClick={handleOpenRequestDialog}>
                {t("patient.uploadFiles.requestPatient")}
              </DropdownMenuItem>
              {isRoleAllowed && appointmentId && (
                <DropdownMenuItem
                  disabled={!appointmentId}
                  data-testid="upload-files-button"
                  className="focus:bg-surface-hovered rounded-md py-2"
                  onClick={() => setIsUploadOpen(true)}>
                  {t("patient.uploadFiles.uploadFiles")}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {isUploadOpen && appointmentId && (
            <UploadDialog appointmentId={appointmentId} onOpenChange={setIsUploadOpen} />
          )}
          {isOpenDialog && (
            <RequestUploadFilesDialog
              contactChannel={contactChannel}
              isContactChannelVerified={isContactChannelVerified}
              onOpenChange={setIsOpenDialog}
              onSendRequest={onSendRequest}
              isSendingRequest={isSendingRequest}
              sendRequestAfterSaving={sendRequestAfterSaving}
            />
          )}
        </div>
      </div>
      {isUploadPending && (
        <Banner type="inline" variant="warning" className="gap-2 px-2" data-testid="file-upload-request-reason-banner">
          <div className="flex w-full flex-wrap gap-2">
            <div>
              <p>{t("patient.uploadFiles.filesUploadRequested")}</p>
              <p>
                {t(`patient.uploadFiles.reasonForNewRequest.label`, {
                  reason: t(`patient.uploadFiles.reasonForNewRequest.${fileUploadRequestData?.type}`),
                })}
              </p>
            </div>
            <Button
              variant="tertiary"
              className="text-on-warning-surface -mt-1 -mb-1 ml-auto h-auto min-h-8 self-start px-3 py-1"
              isLoading={isCancelingRequest}
              onClick={onCancelRequest}
              data-testid="file-upload-request-banner-cancel-button">
              {t("common.button.cancelRequest")}
            </Button>
          </div>
        </Banner>
      )}
    </div>
  )
}

export { RequestUploadFilesMenu }
